<template>
  <div>
    <template v-if="showSyncStatus">
      <SyncStatus
        :loading="syncLoading"
        :success="syncSuccess"
        :platform-logo="platformLogo"
        :total-trades="totalTrades"
      />
    </template>
    <template v-else>
      <!-- Use parent split layout instead of creating a new one -->
      <div class="instructions-side">
        <v-img
          :src="platformLogo"
          contain
          height="100"
          class="mb-4"
          max-width="200"
        ></v-img>
        <h2>{{ $t('stepbystep') }}</h2>
        
        <div class="steps-container">
          <div v-for="(step, index) in formattedSteps" :key="index" class="step-item">
            <div class="step-number">{{ index + 1 }}</div>
            <div class="step-text">
              <template v-if="step.type === 'normal'">
                {{ step.text }}
                <div v-if="step.action" class="action-box d-inline-block mx-2">
                  <span class="instruction">{{ step.action.text }}</span>
                  <template v-if="step.action.type === 'link'">
                    <v-btn icon x-small class="action-icon" style="background-color: transparent !important; color: var(--v-mainBlack-base) !important;" @click="copyToClipboard(step.action.text)">
                      <v-icon small>fa-regular fa-copy</v-icon>
                    </v-btn>
                  </template>
                  <template v-else-if="step.action.type === 'highlight' && downloadLink">
                    <v-btn icon x-small class="action-icon" style="background-color: transparent !important; color: var(--v-mainBlack-base) !important;" @click="downloadPlugin">
                      <v-icon small>fa-regular fa-download</v-icon>
                    </v-btn>
                  </template>
                </div>
                {{ step.suffix }}
              </template>
              <div v-else-if="step.type === 'link' || step.type === 'download'" class="action-box">
                <span class="instruction">{{ step.text }}</span>
                <v-btn icon small class="action-icon" style="background-color: transparent !important; color: var(--v-mainBlack-base) !important;" @click="handleAction(step)">
                  <v-icon>{{ step.type === 'link' ? 'fa-copy' : 'fa-download' }}</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Remove download button from actions -->
    <portal to="onboarding-actions" v-if="!showSyncStatus">
      <v-btn color="primary" :loading="syncLoading" @click="verifySync">
        {{ $t('verifyButton') }}
      </v-btn>
    </portal>
  </div>
</template>

<script>
import SyncStatus from './SyncStatus.vue';
import platformInstructions from '../../config/platform-instructions.json';
import LinkedPlatformRepository from '../../repositories/linkedPlatform';

export default {
  name: 'PluginInstructions',
  components: {
    SyncStatus
  },
  inject: ['portalTarget'], // Inject portal target
  props: {
    platformName: {
      type: String,
      required: true
    },
    platformLogo: {
      type: String,
      required: true
    },
    downloadLink: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showSyncStatus: false,
      syncLoading: false,
      syncSuccess: false,
      totalTrades: 0
    }
  },
  computed: {
    normalizedPlatformKey() {
      // MetaTrader platforms need special handling due to spaces and numbers
      const name = this.platformName.toUpperCase();
      if (name === 'METATRADER 4' || name.includes('MT4')) return 'MT4';
      if (name === 'METATRADER 5' || name.includes('MT5')) return 'MT5';
      
      // Remove all spaces and special characters for other platforms
      return name.replace(/[^A-Z0-9]/g, '');
    },
    platformSteps() {
      const platform = platformInstructions[this.normalizedPlatformKey];
      if (!platform?.steps[this.$i18n.locale]) return [];

      // Replace automation token placeholder in steps
      return platform.steps[this.$i18n.locale].map(step => 
        step.replace('{{automationToken}}', 'aatt' + this.$store.state.user.details.automationToken + 'aatt')
      );
    },
    videoUrl() {
      const platform = platformInstructions[this.normalizedPlatformKey];
      const videoUrl = platform?.videos[this.$i18n.locale];
      
      if (!videoUrl) {
        // eslint-disable-next-line no-console
        console.log('Video URL not found for:', this.normalizedPlatformKey);
        return '';
      }

      // Handle both full URLs and video IDs
      const videoId = videoUrl.includes('youtu.be/') ? 
        videoUrl.split('/').pop() : 
        videoUrl.includes('watch?v=') ? 
          videoUrl.split('v=')[1] : 
          videoUrl;

      return `https://www.youtube.com/embed/${videoId}`;
    },
    formattedSteps() {
      const steps = this.platformSteps;
      return steps.map(step => {
        // Special formats based on platform
        if (step.includes('api.swiset.com')) {
          const beforeText = step.split('https://api.swiset.com')[0];
          const afterText = step.split('https://api.swiset.com')[1] || '';
          return {
            type: 'normal',
            text: beforeText,
            action: {
              type: 'link',
              text: 'https://api.swiset.com',
            },
            suffix: afterText
          };
        }

        if (step.includes('aatt')) {
          const beforeText = step.split('aatt')[0];
          return {
            type: 'normal',
            text: beforeText,
            action: {
              type: 'link',
              text: this.$store.state.user.details.automationToken
            },          };

        }

        // Handle plugin/indicator names
        const pluginMap = {
          'METATRADER4': 'SwisetMT4.ex4',
          'METATRADER5': 'SwisetMT5.ex5',
          'NINJATRADER': 'SwisetNT',
          'ATAS': 'SwisetIndicator',
          "CTRADER": 'SwisetcTrader.algo'
        };
        
        const pluginName = pluginMap[this.normalizedPlatformKey];
        if (pluginName && step.includes(pluginName)) {
          const parts = step.split(pluginName);
          return {
            type: 'normal',
            text: parts[0],
            action: {
              type: 'highlight',
              text: pluginName
            },
            suffix: parts[1] || ''
          };
        }

        // Regular step
        return {
          type: 'normal',
          text: step
        };
      });
    }
  },
  methods: {
    async verifySync() {
      this.syncLoading = true;
      this.showSyncStatus = true;

      try {
        // Add delay to ensure platform connection has been registered
        await new Promise(resolve => setTimeout(resolve, 3000));
        
        const response = await LinkedPlatformRepository.verifyPluginConnection(this.platformName);

        if (response.status < 400) {
          this.syncSuccess = response.data.success;
          this.totalTrades = response.data.totalTrades || 0;
        } else {
          this.syncSuccess = false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error verifying sync:', error);
        this.syncSuccess = false;
      }
      
      this.syncLoading = false;
    },
    handleAction(step) {
      if (step.type === 'link') {
        navigator.clipboard.writeText(step.text);
        // Optional: Show a toast/snackbar for feedback
      } else if (step.type === 'download') {
        window.open(this.downloadLink, '_blank');
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        // Optional: Show feedback toast/snackbar
        this.$emit('copy-success');
      });
    },
    downloadPlugin() {
      window.open(this.downloadLink, '_blank');
    }
  },
  mounted() {
    // Add debug logging on mount
    // eslint-disable-next-line no-console
    console.log('Component mounted with platform:', this.platformName);
    // eslint-disable-next-line no-console
    console.log('Available platforms:', Object.keys(platformInstructions));
  }
}
</script>

<style lang="scss" scoped>
.instructions-side {
  height: 100%;
  overflow-y: auto;
}

// Remove plugin-instructions-container and video-side styles since we're using parent layout

.steps-container {
  margin-top: 32px;
  margin-bottom: 32px;
}

.step-item {
  display: flex;
  margin-bottom: 24px;
  align-items: flex-start;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: var(--v-mainGreen-base);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.step-text {
  font-size: 16px;
  line-height: 1.5;
  font-family: 'Avenir', sans-serif !important;
}

.verification-buttons {
  display: flex;
  align-items: center;
  
  .v-btn + .v-btn {
    margin-left: 16px;
  }
  .v-btn {
    font-family: 'Avenir', sans-serif !important;
  }
}

.action-box {
  background-color: var(--v-lightGray-base);
  border-radius: 4px;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  
  span {
    color: var(--v-mainBlack-base) !important;
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }

  .action-icon {
    opacity: 1; // Changed from 0 to always show
    margin-left: 8px;
    
    &:hover {
      color: var(--v-mainGreen-base);
    }
  }

  // Remove hover effect since buttons are always visible
  &:hover .action-icon {
    opacity: 1;
  }
}

.theme--dark .action-box {
  background-color: #2d2d2d;
  
  span {
    color: var(--v-mediumGray-base);
  }
}
</style>


